import React from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
} from '@headlessui/react';
import { Asset } from '@core/generated/gql-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCancel, faTrash } from '@fortawesome/free-solid-svg-icons';

interface DeleteConfirmationModalProps {
  assetToDelete: Asset | null;
  setAssetToDelete: (asset: Asset | null) => void;
  documentTitle?: string;
  onDelete: (asset: Asset) => void;
  onClose?: () => void;
}

const DeleteConfirmationModal = ({
  assetToDelete,
  setAssetToDelete,
  documentTitle,
  onDelete,
  onClose: onCloseExtra,
}: DeleteConfirmationModalProps) => {
  const displayTitle = documentTitle || assetToDelete?.tag || 'document';

  const onClose = () => {
    setAssetToDelete(null);
    onCloseExtra?.();
  };

  const handleDelete = () => {
    if (!assetToDelete) return;
    onDelete(assetToDelete);
    onClose();
  };

  return (
    <Transition show={assetToDelete !== null} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Background overlay */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        {/* Modal content */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
            >
              <DialogPanel className="flex flex-col overflow-y-auto rounded-xl bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium text-gray-900"
                  >
                    {`Confirm deletion of ${displayTitle}`}
                  </DialogTitle>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="flex w-full flex-col items-center justify-center gap-4 px-4 py-3">
                  <span>
                    Are you sure you want to delete this {displayTitle} file?
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleDelete}
                      className="bg-error-600 hover:bg-error-600 flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-white"
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                      Delete
                    </button>
                    <button
                      onClick={onClose}
                      className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <FontAwesomeIcon icon={faCancel} className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DeleteConfirmationModal;
